import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { LaddaModule } from 'angular2-ladda';
import { ModalModule, TooltipModule } from 'ngx-bootstrap';
import { ANIMATION_TYPES, LoadingModule } from 'ngx-loading';

import { ImportFcdComponent } from 'app/admin/import/fcd/import-fcd.component';
import { ChangelogComponent } from 'app/shared/changelog/changelog.component';
import { SiterecordCommentComponent } from 'app/shared/comments/siterecord-comment.component';
import { SiterecordCommentsComponent } from 'app/shared/comments/siterecord-comments.component';
import { CbmTableComponent } from 'app/shared/datatable/cbm-table.component';
import { DialogContractSettingsComponent } from 'app/shared/dialog-contract-settings/dialog-contract-settings.component';
import { DialogConfirmationComponent } from 'app/shared/dialog-delete-location/dialog-confirmation.component';
import { DialogExportMstComponent } from 'app/shared/dialog-export-mst/dialogexportmst.component';
import { DialogImportFcdComponent } from 'app/shared/dialog-import-fcd/dialog-import-fcd.component';
import { DialogImportMstComponent } from 'app/shared/dialog-import-mst/dialogimportmst.component';
import { DialogReportServicedeskComponent } from 'app/shared/dialog-report-servicedesk/dialog-report-servicedesk.component';
import { FcdComponent } from 'app/shared/fcd/fcd.component';
import { CbmInputSelectComponent } from 'app/shared/input/cbm-input-select/cbm-input-select.component';
import { CbmInputTextComponent } from 'app/shared/input/cbm-input-text/cbm-input-text.component';
import { LinearDetailsFcdComponent } from 'app/shared/linear-details/fcd/linear-details-fcd.component';
import { LinearDetailsGeneralComponent } from 'app/shared/linear-details/general/linear-details-general.component';
import { LinearDetailsOpenlrComponent } from 'app/shared/linear-details/openlr/linear-details-openlr.component';
import { LinearDetailsVildComponent } from 'app/shared/linear-details/vild/linear-details-vild.component';
import { LocationsMapComponent } from 'app/shared/locations-map/locations-map.component';
import { MessageboxComponent } from 'app/shared/messagebox/messagebox.component';
import { LinearOpenlrComponent } from 'app/shared/openlr/linear/linear-openlr.component';
import { OpenlrComponent } from 'app/shared/openlr/openlr.component';
import { PointDetailsGeneralComponent } from 'app/shared/point-details/general/point-details-general.component';
import { PointDetailsIntersectionsComponent } from 'app/shared/point-details/general/point-details-intersections.component';
import { PointDetailsSpecificLaneComponent } from 'app/shared/point-details/general/point-details-specific-lane.component';
import { PointDetailsOpenlrComponent } from 'app/shared/point-details/openlr/point-details-openlr.component';
import { PointDetailsVildComponent } from 'app/shared/point-details/vild/point-details-vild.component';
import { TaskLogComponent } from 'app/shared/tasklog/tasklog.component';
import { ValidationErrorsComponent } from 'app/shared/validationerrors/validationerrors.component';
import { LinearVildComponent } from 'app/shared/vild/linear/linear-vild.component';
import { VildComponent } from 'app/shared/vild/vild.component';

import { DialogCaptureCommentComponent } from './dialog-capture-comment/dialog-capture-comment.component';
import { DialogEditCoordinatesComponent } from './dialog-edit-coordinates/dialog-edit-coordinates.component';
import { DialogEditLocationIdComponent } from './dialog-edit-location-id/dialog-edit-location-id.component';
import { DialogVildApproveComponent } from './dialog-vild-approve/dialog-vild-approve.component';
import { DialogVildDecodeComponent } from './dialog-vild-decode/dialog-vild-decode.component';
import { DialogVildDeleteComponent } from './dialog-vild-delete/dialog-vild-delete.component';
import { DialogVildEncodeComponent } from './dialog-vild-encode/dialog-vild-encode.component';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    LaddaModule.forRoot({
      style: 'zoom-in',
      spinnerColor: 'white',
      spinnerLines: 12
    }),
    NgxDatatableModule,
    LoadingModule.forRoot({
      animationType: ANIMATION_TYPES.circleSwish,
      backdropBackgroundColour: 'rgba(0,0,0,0.3)',
      backdropBorderRadius: '4px',
      primaryColour: '#ffffff',
      secondaryColour: '#ffffff',
      tertiaryColour: '#ffffff'
    }),
    ModalModule.forRoot(),
    TooltipModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  declarations: [
    CbmInputSelectComponent,
    CbmInputTextComponent,
    CbmTableComponent,
    ChangelogComponent,
    DialogContractSettingsComponent,
    DialogConfirmationComponent,
    DialogExportMstComponent,
    DialogImportFcdComponent,
    DialogImportMstComponent,
    DialogReportServicedeskComponent,
    FcdComponent,
    ImportFcdComponent,
    LinearOpenlrComponent,
    LinearDetailsFcdComponent,
    LinearDetailsGeneralComponent,
    LinearDetailsOpenlrComponent,
    LinearDetailsVildComponent,
    LinearVildComponent,
    LocationsMapComponent,
    MessageboxComponent,
    OpenlrComponent,
    PointDetailsGeneralComponent,
    PointDetailsIntersectionsComponent,
    PointDetailsOpenlrComponent,
    PointDetailsSpecificLaneComponent,
    PointDetailsVildComponent,
    SiterecordCommentComponent,
    SiterecordCommentsComponent,
    TaskLogComponent,
    ValidationErrorsComponent,
    VildComponent,
    DialogContractSettingsComponent,
    DialogEditLocationIdComponent,
    DialogEditCoordinatesComponent,
    DialogVildDeleteComponent,
    DialogVildEncodeComponent,
    DialogVildDecodeComponent,
    DialogVildApproveComponent,
    DialogCaptureCommentComponent
  ],
  exports: [
    CbmInputSelectComponent,
    CbmInputTextComponent,
    CbmTableComponent,
    ChangelogComponent,
    DialogContractSettingsComponent,
    DialogConfirmationComponent,
    DialogExportMstComponent,
    DialogImportFcdComponent,
    DialogImportMstComponent,
    DialogReportServicedeskComponent,
    FcdComponent,
    ImportFcdComponent,
    LinearDetailsFcdComponent,
    LinearDetailsGeneralComponent,
    LinearDetailsOpenlrComponent,
    LinearDetailsVildComponent,
    LinearOpenlrComponent,
    LinearVildComponent,
    LocationsMapComponent,
    MessageboxComponent,
    OpenlrComponent,
    PointDetailsGeneralComponent,
    PointDetailsIntersectionsComponent,
    PointDetailsOpenlrComponent,
    PointDetailsSpecificLaneComponent,
    PointDetailsVildComponent,
    SiterecordCommentComponent,
    SiterecordCommentsComponent,
    TaskLogComponent,
    VildComponent,
    ValidationErrorsComponent,
    DialogEditLocationIdComponent,
    DialogVildDeleteComponent,
    DialogVildEncodeComponent,
    DialogVildApproveComponent,
    DialogVildDecodeComponent,
    DialogEditCoordinatesComponent,
    DialogCaptureCommentComponent
  ],
  providers: []
})
export class SharedModule {}



// WEBPACK FOOTER //
// ./src/app/shared/shared.module.ts